import React from 'react';
import { Link } from 'react-router-dom';
import {
 ArrowRight,
 Bell,
 Calendar,
 Crown,
 Shield,
 Video,
} from 'lucide-react';

/**
 * Compact attention / onboarding notices for the vendor dashboard.
 * Renders nothing when there is nothing actionable.
 */
const VendorAttentionStack = ({
 isVendorVerified,
 isVerificationPending,
 onStartVerification,
 hasActiveSubscription,
 subscriptionDetails,
 isExpiringSoon,
 urgencyLevel,
 onRenew,
 onComparePlans,
 pendingConsultationCount,
 onViewConsultations,
 fiveDayReminderBookings,
 eventReminderByDays,
 onOpenReminder,
 onViewBookings,
}) => {
 const items = [];

 if (!isVendorVerified && !isVerificationPending) {
 items.push({
 key: 'verify',
 tone: 'sky',
 Icon: Shield,
 title: 'Complete vendor verification',
 body: 'Submit your business details to unlock full vendor features.',
 primary: { label: 'Start verification', onClick: onStartVerification },
 });
 }

 if (!isVendorVerified && isVerificationPending) {
 items.push({
 key: 'pending-verify',
 tone: 'amber',
 Icon: Shield,
 title: 'Verification under review',
 body: 'Your submission is pending admin approval. We’ll notify you once it’s approved.',
 });
 }

 if (hasActiveSubscription && subscriptionDetails?.is_free_tier) {
 items.push({
 key: 'free',
 tone: 'amber',
 Icon: Crown,
 title: 'You’re on the Free plan',
 body: 'Upgrade for higher limits and premium features like advanced analytics and branding.',
 primary: { label: 'Compare plans', onClick: onComparePlans, href: '/vendor/subscription' },
 });
 }

 if (hasActiveSubscription && subscriptionDetails && isExpiringSoon) {
 const days = subscriptionDetails.days_remaining;
 const critical = urgencyLevel === 'critical';
 items.push({
 key: 'expiring',
 tone: critical ? 'rose' : 'amber',
 Icon: Bell,
 title: critical
 ? 'Subscription expires tomorrow'
 : `Subscription expires in ${days} day${days !== 1 ? 's' : ''}`,
 body: subscriptionDetails.is_free_tier
 ? 'Choose a paid plan to keep premium access without interruption.'
 : 'Renew now to avoid service interruption.',
 primary: {
 label: subscriptionDetails.is_free_tier ? 'Choose a paid plan' : 'Renew subscription',
 onClick: onRenew,
 },
 });
 }

 if (pendingConsultationCount > 0) {
 items.push({
 key: 'consult',
 tone: 'amber',
 Icon: Video,
 title: `${pendingConsultationCount} consultation request${pendingConsultationCount !== 1 ? 's' : ''} awaiting response`,
 body: 'Accept or decline so clients know you’re available.',
 primary: { label: 'View consultations', onClick: onViewConsultations },
 });
 }

 if (fiveDayReminderBookings?.length > 0) {
 const dayEntries = [0, 1, 2, 3, 4, 5]
 .map((days) => {
 const list = eventReminderByDays?.[days] || [];
 if (!list.length) return null;
 const label =
 days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `In ${days} days`;
 return { days, label, count: list.length };
 })
 .filter(Boolean);

 const dayBits = dayEntries
 .map(({ label, count }) => `${label}: ${count}`)
 .join(' · ');

 const soonest = dayEntries[0]?.days;
 const count = fiveDayReminderBookings.length;
 let title;
 if (count === 1 && soonest === 0) {
 title = '1 event today';
 } else if (count === 1 && soonest === 1) {
 title = '1 event tomorrow';
 } else if (count === 1 && soonest != null) {
 title = `1 event in ${soonest} days`;
 } else {
 title = `${count} upcoming events in the next 5 days`;
 }

 items.push({
 key: 'events',
 tone: 'sky',
 Icon: Calendar,
 title,
 body: dayBits || 'Review schedules and prepare for the week ahead.',
 primary: { label: 'Open reminder', onClick: onOpenReminder },
 secondary: { label: 'View bookings', onClick: onViewBookings },
 });
 }

 if (items.length === 0) return null;

 const tones = {
 sky: {
 wrap: 'border-border bg-surface-muted/80',
 icon: 'bg-surface-muted /50 text-ink',
 title: 'text-ink',
 body: 'text-ink/90',
 btn: 'bg-black hover:bg-neutral-800 text-white',
 btnGhost:
 'border border-border text-ink hover:bg-surface-muted/80 ',
 },
 amber: {
 wrap: 'border-amber-200 bg-amber-50/80',
 icon: 'bg-amber-100 text-amber-700',
 title: 'text-amber-950',
 body: 'text-amber-800/90',
 btn: 'bg-amber-800 hover:bg-amber-900 text-white',
 btnGhost:
 'border border-amber-300 text-amber-900 hover:bg-amber-100/80',
 },
 rose: {
 wrap: 'border-red-200 bg-red-50/80',
 icon: 'bg-red-100 text-red-700',
 title: 'text-red-950',
 body: 'text-red-800/90',
 btn: 'bg-red-700 hover:bg-red-800 text-white',
 btnGhost:
 'border border-red-300 text-red-900 hover:bg-red-100/80',
 },
 };

 return (
 <div className="space-y-3">
 <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-tertiary px-0.5">
 Needs attention
 </p>
 <div className="space-y-2.5">
 {items.map((item) => {
 const t = tones[item.tone] || tones.amber;
 const Icon = item.Icon;
 return (
 <div
 key={item.key}
 className={`rounded-lg border p-4 ${t.wrap}`}
 >
 <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
 <div className={`p-2 rounded-xl shrink-0 w-fit ${t.icon}`}>
 <Icon className="w-4 h-4" />
 </div>
 <div className="min-w-0 flex-1">
 <h3 className={`text-sm font-semibold ${t.title}`}>{item.title}</h3>
 <p className={`text-sm mt-1 leading-relaxed ${t.body}`}>{item.body}</p>
 {(item.primary || item.secondary) && (
 <div className="mt-3 flex flex-wrap gap-2">
 {item.primary?.href ? (
 <Link
 to={item.primary.href}
 className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${t.btn}`}
 >
 {item.primary.label}
 <ArrowRight className="w-3.5 h-3.5" />
 </Link>
 ) : item.primary ? (
 <button
 type="button"
 onClick={item.primary.onClick}
 className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${t.btn}`}
 >
 {item.primary.label}
 </button>
 ) : null}
 {item.secondary ? (
 <button
 type="button"
 onClick={item.secondary.onClick}
 className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-surface/70 ${t.btnGhost}`}
 >
 {item.secondary.label}
 </button>
 ) : null}
 </div>
 )}
 </div>
 </div>
 </div>
 );
 })}
 </div>
 </div>
 );
};

export default VendorAttentionStack;
